import { useState, useEffect, useRef } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Cookie, X, ChevronDown, ChevronUp } from "lucide-react";
import { scrubUrl } from "../utils/scrubUrl";
import {
  CONSENT_STORAGE_KEY as STORAGE_KEY,
  getCookieConsent,
  isAnalyticsAllowed,
  isMarketingAllowed,
} from "../utils/consent";
import { applyAnalyticsConsent } from "../utils/track";

// The stored choice is read from utils/consent so track.js can share it
// without importing this component; re-exported here for existing callers.
export { getCookieConsent, isAnalyticsAllowed, isMarketingAllowed };

/**
 * Store cookie consent preferences, then apply them at once — loading GA on a
 * grant, switching it off on a withdrawal from the reopened banner.
 * @param {Object} preferences - { necessary: true, analytics: boolean, marketing: boolean }
 */
const setStoredConsent = (preferences) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...preferences,
        timestamp: new Date().toISOString(),
        version: "1.0",
      }),
    );
  } catch {
    // localStorage not available
  }
  // The first-party log follows the same choice: a grant persists the session
  // id, a withdrawal clears the persisted copy and drops queued analytics
  // events (behaviour-logging FRS FR7 item 3). Before the GA handling, which
  // may reload the page.
  applyAnalyticsConsent(isAnalyticsAllowed());
  if (isAnalyticsAllowed()) {
    initAnalyticsIfConsented();
  } else {
    disableAnalytics();
  }
};

const GA_MEASUREMENT_ID = "G-DG9WYPVEJW";
// gtag.js's own opt-out switch: while it is true, gtag sends nothing to this id.
const GA_DISABLE_KEY = `ga-disable-${GA_MEASUREMENT_ID}`;
let analyticsLoaded = false;

// Indirection so tests can observe the reload; jsdom can't navigate.
export const pageControls = {
  reload: () => window.location.reload(),
};

const gtag = (...args) => window.gtag?.(...args);

/**
 * The parameters every hit carries for "where": the page with no address or
 * coordinates in it (scrubUrl), and the referrer likewise — after a full
 * reload out of the console (sign-out goes to `/`), the referrer is the
 * console's URL, address included.
 */
const pageParams = (href) => {
  const params = { page_location: scrubUrl(href) };
  if (document.referrer) params.page_referrer = scrubUrl(document.referrer);
  return params;
};

/**
 * Send one GA page view for `href` (default: the current URL), scrubbed.
 * GA's own page views are off (`send_page_view: false`), so this is the only
 * way one is recorded: once when GA starts, then on each client-side route
 * change (useGaPageViews). A no-op unless analytics is consented and GA has
 * loaded.
 */
export const sendGaPageView = (href = window.location.href) => {
  if (!analyticsLoaded || !isAnalyticsAllowed()) return;
  const params = pageParams(href);
  // `set` as well, so GA's automatic events (scrolls, outbound clicks) report
  // this page rather than the URL the tag was configured with.
  gtag("set", params);
  gtag("event", "page_view", params);
};

/**
 * Load Google Analytics only after the user has consented (Finding 9 —
 * gtag previously loaded unconditionally from index.html, making this
 * banner a misrepresentation). Called on app start and whenever consent
 * is stored.
 *
 * Consent Mode v2, basic (behaviour-logging TR §11.4): every type defaults to
 * denied before any other gtag call, and only `analytics_storage` is ever
 * granted. The ad types stay denied — there is no ads use.
 */
export const initAnalyticsIfConsented = () => {
  if (!isAnalyticsAllowed()) return;
  if (analyticsLoaded) {
    // Withdrawn earlier in this page's life and now granted again.
    if (window[GA_DISABLE_KEY]) {
      window[GA_DISABLE_KEY] = false;
      gtag("consent", "update", { analytics_storage: "granted" });
      sendGaPageView();
    }
    return;
  }
  analyticsLoaded = true;
  window[GA_DISABLE_KEY] = false;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("consent", "update", { analytics_storage: "granted" });
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    ...pageParams(window.location.href),
    anonymize_ip: true,
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  sendGaPageView();

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

/**
 * Expire GA's `_ga` / `_ga_<id>` cookies. GA sets them on the widest domain it
 * can (`.zatoplan.com` from `www.zatoplan.com`), so try each parent domain as
 * well as the host itself; a domain the browser won't accept is ignored.
 */
const clearGaCookies = () => {
  const names = document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((n) => n === "_ga" || n.startsWith("_ga_"));
  if (!names.length) return;
  const labels = window.location.hostname.split(".");
  const domains = [null, ...labels.map((_, i) => labels.slice(i).join("."))];
  const expired = "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  names.forEach((name) =>
    domains.forEach((domain) => {
      document.cookie = domain ? `${name}${expired}; domain=${domain}` : `${name}${expired}`;
    }),
  );
};

/**
 * Analytics withdrawn: tell gtag consent is denied, switch it off, drop its
 * cookies — and, if it had loaded on this page, reload.
 *
 * The reload is the part that actually stops it. A loaded gtag.js can't be
 * unloaded, and with consent denied it keeps sending cookieless pings. The
 * opt-out flag only covers our own id, and the Google tag forwards to a second
 * GA4 destination configured in the GA admin: the headless check (behaviour-
 * logging task 1.4) saw that destination send a page view with the raw URL,
 * address included, after a withdrawal. After the reload, consent is stored
 * as declined, so GA never loads.
 */
const disableAnalytics = () => {
  clearGaCookies();
  if (!analyticsLoaded) return;
  gtag("consent", "update", { analytics_storage: "denied" });
  window[GA_DISABLE_KEY] = true;
  pageControls.reload();
};

/** Tests only: forget that GA was loaded, since that is module state. */
export const resetAnalyticsForTests = () => {
  analyticsLoaded = false;
};

const OPEN_EVENT = "zatoplan:open-cookie-preferences";

/**
 * Reopen the banner with the visitor's current choices filled in, from any
 * "Cookie preferences" control. The banner is mounted once, in App, and
 * listens for this.
 */
export const openCookiePreferences = () => {
  window.dispatchEvent(new Event(OPEN_EVENT));
};

const DEFAULT_PREFERENCES = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
};

/**
 * CookieConsent - GDPR compliant cookie consent banner
 * Appears at the bottom of the screen until user makes a choice
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Reopened from a "Cookie preferences" control, over a choice already made.
  const [reopened, setReopened] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const cardRef = useRef(null);

  // Check for existing consent on mount
  useEffect(() => {
    const existingConsent = getCookieConsent();
    if (!existingConsent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
    // Returning visitor who already consented: honor it
    initAnalyticsIfConsented();
  }, []);

  // Reopen on request, details open so the current choices are what you see.
  useEffect(() => {
    const open = () => {
      const stored = getCookieConsent();
      setPreferences({
        necessary: true,
        analytics: stored?.analytics === true,
        marketing: stored?.marketing === true,
      });
      setReopened(Boolean(stored));
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  // A reopened banner was asked for, so take focus to it.
  useEffect(() => {
    if (visible && reopened) cardRef.current?.focus();
  }, [visible, reopened]);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setStoredConsent(allAccepted);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setStoredConsent(onlyNecessary);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    setStoredConsent(preferences);
    setVisible(false);
  };

  // First visit: closing is a rejection, as it always was. Reopened: closing
  // keeps the choice already stored.
  const handleClose = () => {
    if (reopened) {
      setVisible(false);
    } else {
      handleRejectAll();
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-in slide-in-from-bottom duration-300">
      <Card
        ref={cardRef}
        tabIndex={-1}
        role="region"
        aria-labelledby="cookie-consent-title"
        className="max-w-2xl mx-auto shadow-2xl border bg-background outline-none"
      >
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 id="cookie-consent-title" className="font-semibold text-base mb-1">
                Cookie Preferences
              </h3>
              <p className="text-sm text-muted-foreground">
                We use cookies to enhance your experience. Some are essential for the site
                to function, while others help us understand how you use our service.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex-shrink-0"
              onClick={handleClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Expandable Details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            {showDetails ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            <span>{showDetails ? "Hide" : "Show"} cookie details</span>
          </button>

          {showDetails && (
            <div className="space-y-3 mb-4 p-4 bg-muted/50 rounded-lg">
              {/* Necessary Cookies */}
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Essential Cookies</div>
                  <p className="text-xs text-muted-foreground">
                    Required for the website to function. Cannot be disabled.
                  </p>
                </div>
              </label>

              {/* Analytics Cookies */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences({ ...preferences, analytics: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-primary"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Analytics Cookies</div>
                  <p className="text-xs text-muted-foreground">
                    Help us understand how visitors interact with our website to improve
                    our service.
                  </p>
                </div>
              </label>

              {/* Marketing Cookies */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) =>
                    setPreferences({ ...preferences, marketing: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-primary"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Marketing Cookies</div>
                  <p className="text-xs text-muted-foreground">
                    Used to deliver relevant advertisements and track their effectiveness.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            {showDetails ? (
              <>
                <Button variant="outline" className="flex-1" onClick={handleRejectAll}>
                  Reject All
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleSavePreferences}
                >
                  Save Preferences
                </Button>
                <Button className="flex-1" onClick={handleAcceptAll}>
                  Accept All
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="flex-1" onClick={handleRejectAll}>
                  Reject All
                </Button>
                <Button className="flex-1" onClick={handleAcceptAll}>
                  Accept All
                </Button>
              </>
            )}
          </div>

          {/* Privacy Policy Link */}
          <p className="text-xs text-muted-foreground text-center mt-4">
            By using our site, you acknowledge that you have read our{" "}
            <a href="/privacy" className="underline hover:text-foreground">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
